import { useState } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { History, Loader2, ScanSearch } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ConfigLine } from '@/components/ConfigText';
import { RestoreWarnings } from '@/components/RestoreWarnings';
import { useConfigTextCredentials } from '@/hooks/useCredentialReferences';
import { useConfirm } from '@/hooks/useConfirm';
import { usePermission } from '@/hooks/usePermission';
import {
  isRestoreInProgress,
  useConfigRestores,
  useConfigRestoreSupport,
  usePreviewRestore,
  useRestoreSnapshot,
} from '@/hooks/useConfigSnapshots';

interface ConfigRestoreControlsProps {
  deviceId: string;
  snapshotId: string;
  /** When the snapshot was captured, for the confirmation. */
  capturedAt: string;
}

/** What the device costs to restore, in words, from its profile's restart wait. */
function downtimeWarning(restartWaitSecs: number | null): string {
  if (restartWaitSecs == null) {
    return 'The device applies the config in place.';
  }
  const minutes = Math.max(1, Math.round(restartWaitSecs / 60));
  return (
    `The device restarts to apply it and can be unreachable for up to ${String(minutes)} ` +
    `minute${minutes === 1 ? '' : 's'}. Anything that reaches the network through it, ` +
    'including a gateway, loses its connection until the device is back.'
  );
}

function errorMessage(e: unknown): string {
  if (axios.isAxiosError<{ description?: string; error?: string } | undefined>(e)) {
    const body = e.response?.data;
    return body?.description ?? body?.error ?? e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * Preview and restore controls for one snapshot. Rendered only when the
 * device's vendor profile can restore a config; disabled, with the reason,
 * when the user may not or a restore is already under way.
 */
export default function ConfigRestoreControls({
  deviceId,
  snapshotId,
  capturedAt,
}: ConfigRestoreControlsProps) {
  const { data: support } = useConfigRestoreSupport(deviceId);
  const { data: restores } = useConfigRestores(deviceId);
  const canRestore = usePermission('device:restore_config');
  const preview = usePreviewRestore(deviceId);
  const previewCredentials = useConfigTextCredentials([preview.data?.diff, preview.data?.error]);
  const restore = useRestoreSnapshot(deviceId);
  const { confirm, confirmDialog } = useConfirm();
  const [notice, setNotice] = useState<string | null>(null);

  if (!support?.supported) {
    return null;
  }

  const inProgress = restores?.some(isRestoreInProgress) ?? false;
  const disabledReason = !canRestore
    ? 'You need the "Restore device config" permission.'
    : inProgress
      ? 'A restore of this device is already in progress.'
      : null;

  const onRestore = async () => {
    setNotice(null);
    const ok = await confirm({
      title: 'Restore this configuration?',
      message:
        `The device's whole configuration is replaced with the one captured ` +
        `${formatDistanceToNow(new Date(capturedAt), { addSuffix: true })}. ` +
        `${downtimeWarning(support.restartWaitSecs)} ` +
        'Use Preview first to see what changes.',
      confirmText: 'Restore',
      isDanger: true,
    });
    if (!ok) return;
    restore.mutate(snapshotId, {
      onSuccess: () => {
        setNotice(
          'Restore started. Its progress and result appear under Restores on this page.',
        );
      },
    });
  };

  return (
    <div className="mb-2 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          disabled={!!disabledReason || preview.isPending}
          title={disabledReason ?? undefined}
          onClick={() => {
            setNotice(null);
            preview.mutate(snapshotId);
          }}
        >
          {preview.isPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />
          ) : (
            <ScanSearch className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
          )}
          Preview restore
        </Button>
        <Button
          variant="destructive"
          size="sm"
          disabled={!!disabledReason || restore.isPending}
          title={disabledReason ?? undefined}
          onClick={() => {
            void onRestore();
          }}
        >
          <History className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
          Restore this config
        </Button>
        {disabledReason ? (
          <span className="text-[11px] text-muted-foreground">{disabledReason}</span>
        ) : null}
      </div>

      {preview.isError ? (
        <Alert variant="destructive" className="text-xs">
          <AlertDescription>Preview failed: {errorMessage(preview.error)}</AlertDescription>
        </Alert>
      ) : null}
      {preview.data?.error ? (
        <Alert variant="destructive" className="text-xs">
          <AlertDescription>Preview failed: {preview.data.error}</AlertDescription>
        </Alert>
      ) : null}
      {preview.data?.warnings ? <RestoreWarnings warnings={preview.data.warnings} /> : null}
      {preview.data && !preview.data.error ? (
        preview.data.diff ? (
          <div>
            <p className="text-[11px] text-muted-foreground mb-1">
              What a restore changes on the device (+ added, − removed):
            </p>
            <pre className="max-h-64 overflow-auto rounded-md bg-muted/30 p-2 text-xs leading-relaxed font-mono">
              {preview.data.diff.split('\n').map((line, i) => (
                <span
                  key={i}
                  className={
                    line.startsWith('+')
                      ? 'block text-success'
                      : line.startsWith('-')
                        ? 'block text-destructive'
                        : 'block'
                  }
                >
                  <ConfigLine line={line} credentials={previewCredentials} />
                </span>
              ))}
            </pre>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            The device already has this configuration. A restore would change nothing.
          </p>
        )
      ) : null}

      {restore.isError ? (
        <Alert variant="destructive" className="text-xs">
          <AlertDescription>Could not start the restore: {errorMessage(restore.error)}</AlertDescription>
        </Alert>
      ) : null}
      {notice ? <p className="text-xs text-muted-foreground">{notice}</p> : null}
      {confirmDialog}
    </div>
  );
}
